import { DragDropModule } from '@angular/cdk/drag-drop';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import type { ModuleWithProviders } from '@angular/core';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HammerModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { OpenNativeSettings } from '@awesome-cordova-plugins/open-native-settings/ngx';
import { DirectivesModule } from '@freelancer/directives';
import { FreelancerAsyncPipeModule, PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { MaxLinesDirective } from '@freelancer/ui/text/max-lines.directive';
import { AnchorScrollModule } from './anchor-scroll/anchor-scroll.module';
import { AnimatedSvgModule } from './animated-svg/animated-svg.module';
import { AnnotationIndicatorComponent } from './annotation-indicator/annotation-indicator.component';
import { BadgeComponent } from './badge/badge.component';
import { BannerAlertComponent } from './banner-alert/banner-alert.component';
import {
  BannerAnnouncementButtonsComponent,
  BannerAnnouncementComponent,
  BannerAnnouncementMessageComponent,
} from './banner-announcement';
import {
  BannerUpsellBackgroundImageComponent,
  BannerUpsellButtonComponent,
  BannerUpsellComponent,
  BannerUpsellDescriptionComponent,
  BannerUpsellDisclaimerComponent,
  BannerUpsellPictureComponent,
  BannerUpsellSmallTitleComponent,
  BannerUpsellTitleComponent,
} from './banner-upsell';
import { BreadcrumbsComponent, BreadcrumbsItemComponent } from './breadcrumbs';
import { BudgetComponent } from './budget.component';
import { ButtonComponent } from './button/button.component';
import { CalloutContentComponent } from './callout/callout-content.component';
import { CalloutTriggerComponent } from './callout/callout-trigger.component';
import { CalloutComponent } from './callout/callout.component';
import { CameraComponent } from './camera/camera.component';
import { CameraInputComponent } from './camera-input/camera-input.component';
import {
  CardComponent,
  CardHeaderRightComponent,
  CardHeaderSecondaryComponent,
  CardHeaderTitleComponent,
} from './card';
import { CardListComponent, CardListItemComponent } from './card-list';
import {
  CarouselComponent,
  CarouselItemComponent,
  CarouselNextButtonComponent,
  CarouselPrevButtonComponent,
} from './carousel';
import { ChartBarComponent } from './chart/chart-bar.component';
import { ChartComponent } from './chart/chart.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { CheckboxCardComponent } from './checkbox-card/checkbox-card.component';
import { ColumnComponent } from './column/column.component';
import { ContainerComponent } from './container/container.component';
import { CurrencyInputComponent } from './currency-input';
import {
  DisplayCardComponent,
  DisplayCardContentComponent,
  DisplayCardFooterLeftComponent,
  DisplayCardFooterRightComponent,
  DisplayCardHeaderLeftComponent,
  DisplayCardHeaderRightComponent,
  DisplayCardImageComponent,
} from './display-card';
import { DrawerComponent } from './drawer/drawer.component';
import { DropdownFilterComponent } from './dropdown-filter/dropdown-filter.component';
import { FileDisplayComponent } from './file-display/file-display.component';
import { FileSelectComponent } from './file-select/file-select.component';
import { FileViewerComponent } from './file-viewer/file-viewer.component';
import { FlagComponent } from './flag/flag.component';
import { FloatingActionComponent } from './floating-action/floating-action.component';
import { GenericErrorComponent } from './generic-error/generic-error.component';
import { GridComponent } from './grid/grid.component';
import { HighlightCalloutComponent } from './highlight/highlight-callout.component';
import { HighlightComponent } from './highlight/highlight.component';
import { HighlightTextComponent } from './highlight-text/highlight-text.component';
import { HrComponent } from './hr/hr.component';
import { IconModule } from './icon/icon.module';
import { InfiniteScrollContainerDirective } from './infinite-scroll/infinite-scroll-container.directive';
import { InfiniteScrollComponent } from './infinite-scroll/infinite-scroll.component';
import { InputComponent } from './input/input.component';
import { NumberValueAccessorDirective } from './input/value-accessors/number-value-accessor.directive';
import { LabelComponent } from './label/label.component';
import { LinkModule } from './link/link.module';
import { ListComponent } from './list/list.component';
import {
  ListItemBodyComponent,
  ListItemComponent,
  ListItemHeaderComponent,
} from './list-item';
import { LoadingTemplateComponent } from './loading-template/loading-template.component';
import { LocalizedDateFns } from './localized-date-fns.service';
import { LocationInputComponent } from './location-input/location-input.component';
import { LogoComponent } from './logo/logo.component';
import { MapComponent } from './map/map.component';
import type { LightGalleryConfig } from './media-viewer/media-viewer-light-gallery';
import { LIGHTGALLERY_CONFIG } from './media-viewer/media-viewer-light-gallery';
import { ModalTitleDirective } from './modal/modal-title.directive';
import { MoreOptionsComponent, MoreOptionsItemComponent } from './more-options';
import { MultiSelectComponent } from './multi-select/multi-select.component';
import { MultipleLocationInputComponent } from './multiple-location-input/multiple-location-input.component';
import { NewFeatureTagComponent } from './new-feature-tag';
import { NotificationIndicatorComponent } from './notification-indicator/notification-indicator.component';
import { OnlineIndicatorComponent } from './online-indicator/online-indicator.component';
import { OtpInputComponent } from './otp-input/otp-input.component';
import {
  PageLayoutComponent,
  PageLayoutPrimaryComponent,
  PageLayoutSecondaryComponent,
  PageLayoutSingleComponent,
} from './page-layout';
import { PaginationComponent } from './pagination/pagination.component';
import { PictureComponent } from './picture/picture.component';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import {
  ProgressStepCalloutComponent,
  ProgressStepContentComponent,
  ProgressStepItemComponent,
} from './progress-steps';
import { ProgressStepsComponent } from './progress-steps/progress-steps.component';
import { ProgressSubstepComponent } from './progress-steps/progress-substep.component';
import { RadioComponent } from './radio/radio.component';
import { RatingComponent } from './rating/rating.component';
import { RelativeTimeComponent } from './relative-time/relative-time.component';
import { RibbonComponent } from './ribbon/ribbon.component';
import { ScrollableContentComponent } from './scrollable-content/scrollable-content.component';
import { SearchComponent } from './search/search.component';
import {
  SectionHomepageEntryComponent,
  SectionHomepageEntryDescriptionComponent,
  SectionHomepageEntryHeadingComponent,
  SectionHomepageEntryLinkComponent,
  SectionHomepageFeatureBottomLeftComponent,
  SectionHomepageFeatureBottomRightComponent,
  SectionHomepageFeatureComponent,
  SectionHomepageFeatureFooterComponent,
  SectionHomepageFeatureTitleComponent,
  SectionHomepageFeatureTopLeftComponent,
  SectionHomepageFeatureTopRightComponent,
} from './section-homepage-feature';
import { SelectComponent } from './select/select.component';
import { ShareButtonsComponent } from './share-buttons/share-buttons.component';
import { ShareLinkComponent } from './share-link/share-link.component';
import { SidebarFooterComponent } from './sidebar/sidebar-footer.component';
import { SidebarHeadingDirective } from './sidebar/sidebar-heading.directive';
import { SidebarItemComponent } from './sidebar/sidebar-item.component';
import { SidebarSubheadingDirective } from './sidebar/sidebar-subheading.directive';
import { SidebarComponent } from './sidebar/sidebar.component';
import { SidebarCustomTemplateDirective } from './sidebar/sidebar.directive';
import { SliderComponent } from './slider/slider.component';
import { SocialButtonsComponent } from './social-buttons/social-buttons.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { SplitButtonOptionComponent } from './split-button/split-button-option.component';
import { SplitButtonPrimaryComponent } from './split-button/split-button-primary.component';
import { SplitButtonComponent } from './split-button/split-button.component';
import { StickyModule } from './sticky/sticky.module';
import { StickyFooterBodyComponent } from './sticky-footer/sticky-footer-body.component';
import { StickyFooterWrapperComponent } from './sticky-footer/sticky-footer-wrapper.component';
import { StickyFooterComponent } from './sticky-footer/sticky-footer.component';
import { StoryHeaderComponent } from './story-header/fl-story-header.component';
import {
  TableColumnComponent,
  TableComponent,
  TableExpandableContentComponent,
} from './table';
import {
  TabItemActionComponent,
  TabItemComponent,
  TabsComponent,
} from './tabs';
import { TagComponent } from './tag/tag.component';
import { TagStatusComponent } from './tag-status/tag-status.component';
import { TextareaComponent } from './textarea/textarea.component';
import {
  ThumbnailViewerComponent,
  ThumbnailViewerImageComponent,
} from './thumbnail-viewer';
import { ToastAlertContainerComponent } from './toast-alert/toast-alert-container.component';
import { ToastAlertComponent } from './toast-alert/toast-alert.component';
import { TOAST_ALERT_COMPONENT } from './toast-alert/toast-alert.types';
import { ToggleComponent } from './toggle/toggle.component';
import { ToggleButtonsComponent } from './toggle-buttons/toggle-buttons.component';
import { UI_CONFIG } from './ui.config';
import type { UiConfig } from './ui.interface';
import { UnreadIndicatorComponent } from './unread-indicator/unread-indicator.component';
import { UserActivityStatusComponent } from './user-activity-status/user-activity-status.component';
import { UserAvatarComponent } from './user-avatar/user-avatar.component';
import { ValidationErrorComponent } from './validation-error/validation-error.component';
import { VideoModule } from './video/video.module';
import { ViewHeaderComponent } from './view-header/view-header.component';
import { WidgetButtonComponent, WidgetComponent } from './widget';

@NgModule({
  declarations: [],
  exports: [
    AnchorScrollModule,
    AnimatedSvgModule,
    AnnotationIndicatorComponent,
    BadgeComponent,
    BannerAlertComponent,
    BannerAnnouncementButtonsComponent,
    BannerAnnouncementComponent,
    BannerAnnouncementMessageComponent,
    BannerUpsellBackgroundImageComponent,
    BannerUpsellButtonComponent,
    BannerUpsellComponent,
    BannerUpsellDescriptionComponent,
    BannerUpsellDisclaimerComponent,
    BannerUpsellPictureComponent,
    BannerUpsellSmallTitleComponent,
    BannerUpsellTitleComponent,
    BreadcrumbsComponent,
    BreadcrumbsItemComponent,
    BudgetComponent,
    ButtonComponent,
    CalloutComponent,
    CalloutContentComponent,
    CalloutTriggerComponent,
    CameraComponent,
    CameraInputComponent,
    CardComponent,
    CardHeaderRightComponent,
    CardHeaderSecondaryComponent,
    CardHeaderTitleComponent,
    CardListComponent,
    CardListItemComponent,
    CarouselComponent,
    CarouselItemComponent,
    CarouselNextButtonComponent,
    CarouselPrevButtonComponent,
    ChartBarComponent,
    ChartComponent,
    CheckboxCardComponent,
    CheckboxComponent,
    ColumnComponent,
    ContainerComponent,
    CurrencyInputComponent,
    DirectivesModule,
    DisplayCardComponent,
    DisplayCardContentComponent,
    DisplayCardFooterLeftComponent,
    DisplayCardFooterRightComponent,
    DisplayCardHeaderLeftComponent,
    DisplayCardHeaderRightComponent,
    DisplayCardImageComponent,
    DrawerComponent,
    DropdownFilterComponent,
    FileDisplayComponent,
    FileSelectComponent,
    FileViewerComponent,
    FlagComponent,
    FloatingActionComponent,
    FreelancerAsyncPipeModule,
    GenericErrorComponent,
    GridComponent,
    HighlightCalloutComponent,
    HighlightComponent,
    HighlightTextComponent,
    HrComponent,
    IconModule,
    InfiniteScrollComponent,
    InfiniteScrollContainerDirective,
    InputComponent,
    LabelComponent,
    LinkModule,
    ListComponent,
    ListItemBodyComponent,
    ListItemComponent,
    ListItemHeaderComponent,
    LoadingTemplateComponent,
    LocationInputComponent,
    LogoComponent,
    MapComponent,
    MaxLinesDirective,
    ModalTitleDirective,
    MoreOptionsComponent,
    MoreOptionsItemComponent,
    MultiSelectComponent,
    MultipleLocationInputComponent,
    NewFeatureTagComponent,
    NotificationIndicatorComponent,
    OnlineIndicatorComponent,
    OtpInputComponent,
    PageLayoutComponent,
    PageLayoutPrimaryComponent,
    PageLayoutSecondaryComponent,
    PageLayoutSingleComponent,
    PaginationComponent,
    PictureComponent,
    ProgressBarComponent,
    ProgressStepCalloutComponent,
    ProgressStepContentComponent,
    ProgressStepItemComponent,
    ProgressStepsComponent,
    ProgressSubstepComponent,
    RadioComponent,
    RatingComponent,
    ReactiveFormsModule,
    RelativeTimeComponent,
    RibbonComponent,
    ScrollableContentComponent,
    SearchComponent,
    SectionHomepageEntryComponent,
    SectionHomepageEntryDescriptionComponent,
    SectionHomepageEntryHeadingComponent,
    SectionHomepageEntryLinkComponent,
    SectionHomepageFeatureBottomLeftComponent,
    SectionHomepageFeatureBottomRightComponent,
    SectionHomepageFeatureComponent,
    SectionHomepageFeatureFooterComponent,
    SectionHomepageFeatureTitleComponent,
    SectionHomepageFeatureTopLeftComponent,
    SectionHomepageFeatureTopRightComponent,
    SelectComponent,
    ShareButtonsComponent,
    ShareLinkComponent,
    SidebarComponent,
    SidebarCustomTemplateDirective,
    SidebarFooterComponent,
    SidebarHeadingDirective,
    SidebarItemComponent,
    SidebarSubheadingDirective,
    SliderComponent,
    SocialButtonsComponent,
    SpinnerComponent,
    SplitButtonComponent,
    SplitButtonOptionComponent,
    SplitButtonPrimaryComponent,
    StickyFooterBodyComponent,
    StickyFooterComponent,
    StickyFooterWrapperComponent,
    StickyModule,
    StoryHeaderComponent,
    TabItemActionComponent,
    TabItemComponent,
    TableColumnComponent,
    TableComponent,
    TableExpandableContentComponent,
    TabsComponent,
    TagComponent,
    TagStatusComponent,
    TextareaComponent,
    ThumbnailViewerComponent,
    ThumbnailViewerImageComponent,
    ToastAlertComponent,
    ToastAlertContainerComponent,
    ToggleButtonsComponent,
    ToggleComponent,
    UnreadIndicatorComponent,
    UserActivityStatusComponent,
    UserAvatarComponent,
    ValidationErrorComponent,
    VideoModule,
    ViewHeaderComponent,
    WidgetButtonComponent,
    WidgetComponent,
  ],
  imports: [
    BannerAlertComponent,
    BannerUpsellBackgroundImageComponent,
    BannerUpsellButtonComponent,
    BannerUpsellComponent,
    BannerUpsellDescriptionComponent,
    BannerUpsellDisclaimerComponent,
    BannerUpsellPictureComponent,
    BannerUpsellSmallTitleComponent,
    BannerUpsellTitleComponent,
    ButtonComponent,
    CalloutComponent,
    CalloutContentComponent,
    CameraInputComponent,
    CardComponent,
    CardHeaderRightComponent,
    CardHeaderSecondaryComponent,
    CardHeaderTitleComponent,
    CarouselComponent,
    CarouselItemComponent,
    CarouselNextButtonComponent,
    CarouselPrevButtonComponent,
    CheckboxCardComponent,
    CurrencyInputComponent,
    DisplayCardComponent,
    DisplayCardContentComponent,
    DisplayCardFooterLeftComponent,
    DisplayCardFooterRightComponent,
    DisplayCardHeaderLeftComponent,
    DisplayCardHeaderRightComponent,
    DisplayCardImageComponent,
    DrawerComponent,
    DropdownFilterComponent,
    FileSelectComponent,
    FloatingActionComponent,
    HighlightComponent,
    InfiniteScrollComponent,
    InfiniteScrollContainerDirective,
    InputComponent,
    ListItemComponent,
    LocationInputComponent,
    ModalTitleDirective,
    MoreOptionsComponent,
    MoreOptionsItemComponent,
    MultipleLocationInputComponent,
    NumberValueAccessorDirective,
    PaginationComponent,
    ProgressStepItemComponent,
    SearchComponent,
    ShareButtonsComponent,
    ShareLinkComponent,
    SidebarComponent,
    SidebarCustomTemplateDirective,
    SidebarFooterComponent,
    SidebarHeadingDirective,
    SidebarSubheadingDirective,
    SocialButtonsComponent,
    SplitButtonComponent,
    SplitButtonOptionComponent,
    SplitButtonPrimaryComponent,
    TableColumnComponent,
    TableComponent,
    TableExpandableContentComponent,
    TextareaComponent,
    ToastAlertComponent,
    ToastAlertContainerComponent,
    ViewHeaderComponent,
    WidgetButtonComponent,
    WidgetComponent,
    AnchorScrollModule,
    AnimatedSvgModule,
    AnnotationIndicatorComponent,
    BadgeComponent,
    BannerAnnouncementButtonsComponent,
    BannerAnnouncementComponent,
    BannerAnnouncementMessageComponent,
    BreadcrumbsComponent,
    BreadcrumbsItemComponent,
    BudgetComponent,
    CalloutTriggerComponent,
    CameraComponent,
    CardListComponent,
    CardListItemComponent,
    ChartBarComponent,
    ChartComponent,
    CheckboxComponent,
    ColumnComponent,
    CommonModule,
    ContainerComponent,
    DirectivesModule,
    DragDropModule,
    FileDisplayComponent,
    FileViewerComponent,
    FlagComponent,
    FreelancerAsyncPipeModule,
    GenericErrorComponent,
    GoogleMapsModule,
    GridComponent,
    HammerModule,
    HighlightCalloutComponent,
    HighlightTextComponent,
    HrComponent,
    IconModule,
    LabelComponent,
    LinkModule,
    ListComponent,
    ListItemBodyComponent,
    ListItemHeaderComponent,
    LoadingTemplateComponent,
    LogoComponent,
    MapComponent,
    MaxLinesDirective,
    MultiSelectComponent,
    NewFeatureTagComponent,
    NotificationIndicatorComponent,
    ObserversModule,
    OnlineIndicatorComponent,
    OtpInputComponent,
    OverlayModule,
    PageLayoutComponent,
    PageLayoutPrimaryComponent,
    PageLayoutSecondaryComponent,
    PageLayoutSingleComponent,
    PictureComponent,
    PipesModule,
    PortalModule,
    ProgressBarComponent,
    ProgressStepCalloutComponent,
    ProgressStepContentComponent,
    ProgressStepsComponent,
    ProgressSubstepComponent,
    RadioComponent,
    RatingComponent,
    /**
     * FIXME: T280638 - In Angular 15, a breaking change was introduced:
     * https://angular.io/guide/update-to-version-15#setdisabledstate-is-always-called-when-a-controlvalueaccessor-is-attached)
     *
     * This caused [attr.disabled] to not disable the `<select>` element as it is in sync with FormControl. Disabling the
     * FormControl will disable the `<select>` element, but it's value be omitted when using `FormGroup.value`, and this
     * may cause regression issues.
     *
     * https://github.com/angular/angular/issues/48350#issuecomment-1405900515
     */
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    RelativeTimeComponent,
    RibbonComponent,
    RouterModule,
    ScrollableContentComponent,
    ScrollingModule,
    SectionHomepageEntryComponent,
    SectionHomepageEntryDescriptionComponent,
    SectionHomepageEntryHeadingComponent,
    SectionHomepageEntryLinkComponent,
    SectionHomepageFeatureBottomLeftComponent,
    SectionHomepageFeatureBottomRightComponent,
    SectionHomepageFeatureComponent,
    SectionHomepageFeatureFooterComponent,
    SectionHomepageFeatureTitleComponent,
    SectionHomepageFeatureTopLeftComponent,
    SectionHomepageFeatureTopRightComponent,
    SelectComponent,
    SidebarItemComponent,
    SliderComponent,
    SpinnerComponent,
    StickyFooterBodyComponent,
    StickyFooterComponent,
    StickyFooterWrapperComponent,
    StickyModule,
    StoryHeaderComponent,
    TabItemActionComponent,
    TabItemComponent,
    TabsComponent,
    TagComponent,
    TagStatusComponent,
    ThumbnailViewerComponent,
    ThumbnailViewerImageComponent,
    ToggleButtonsComponent,
    ToggleComponent,
    TrackingModule,
    UnreadIndicatorComponent,
    UserActivityStatusComponent,
    UserAvatarComponent,
    ValidationErrorComponent,
    VideoModule,
    MatTooltipModule,
  ],
  // /!\ DO NOT ADD PROVIDERS HERE
})
export class UiModule {
  static initialize(config: UiConfig): ModuleWithProviders<UiModule> {
    return {
      ngModule: UiModule,
      providers: [
        {
          provide: LIGHTGALLERY_CONFIG,
          useValue: {
            licenseKey: config.lightGalleryLicenseKey,
          } as LightGalleryConfig,
        },
        { provide: UI_CONFIG, useValue: config },
        LocalizedDateFns,
        {
          provide: TOAST_ALERT_COMPONENT,
          useValue: ToastAlertComponent,
        },
        OpenNativeSettings,
      ],
    };
  }
}
